import {
  listEventsByContactId as publicListEventsByContactId,
  listEventsByMemberId as publicListEventsByMemberId,
  getEvent as publicGetEvent,
  listEvents as publicListEvents,
  queryEvents as publicQueryEvents,
  typedQueryEvents as publicTypedQueryEvents,
  createEvent as publicCreateEvent,
  bulkCreateEvent as publicBulkCreateEvent,
  updateEvent as publicUpdateEvent,
  bulkUpdateEvent as publicBulkUpdateEvent,
  restoreEventDefaults as publicRestoreEventDefaults,
  splitRecurringEvent as publicSplitRecurringEvent,
  cancelEvent as publicCancelEvent,
  bulkCancelEvent as publicBulkCancelEvent,
} from './calendar-v3-event-events.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  EventQuery,
  EventsQueryBuilder,
  QueryEventsOptions,
  typedQueryEvents as universalTypedQueryEvents,
} from './calendar-v3-event-events.universal.js';
import { onEventCancelled as publicOnEventCancelled } from './calendar-v3-event-events.public.js';
import { onEventCreated as publicOnEventCreated } from './calendar-v3-event-events.public.js';
import { onEventRecurringSplit as publicOnEventRecurringSplit } from './calendar-v3-event-events.public.js';
import { onEventUpdated as publicOnEventUpdated } from './calendar-v3-event-events.public.js';

function customQueryEvents(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: (options?: QueryEventsOptions) =>
      publicQueryEvents(httpClient)(options),
    typedQueryFunction: (query: EventQuery, options?: QueryEventsOptions) =>
      publicTypedQueryEvents(httpClient)(query, options),
    hasOptionsParameter: true,
  });

  function overloadedQuery(
    query: EventQuery,
    options?: QueryEventsOptions
  ): ReturnType<typeof universalTypedQueryEvents>;
  function overloadedQuery(options?: QueryEventsOptions): EventsQueryBuilder;
  function overloadedQuery(
    queryOrOptions?: EventQuery | QueryEventsOptions,
    options?: QueryEventsOptions
  ): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const listEventsByContactId: MaybeContext<
  BuildRESTFunction<typeof publicListEventsByContactId> &
    typeof publicListEventsByContactId
> = /*#__PURE__*/ createRESTModule(publicListEventsByContactId);
export const listEventsByMemberId: MaybeContext<
  BuildRESTFunction<typeof publicListEventsByMemberId> &
    typeof publicListEventsByMemberId
> = /*#__PURE__*/ createRESTModule(publicListEventsByMemberId);
export const getEvent: MaybeContext<
  BuildRESTFunction<typeof publicGetEvent> & typeof publicGetEvent
> = /*#__PURE__*/ createRESTModule(publicGetEvent);
export const listEvents: MaybeContext<
  BuildRESTFunction<typeof publicListEvents> & typeof publicListEvents
> = /*#__PURE__*/ createRESTModule(publicListEvents);
export const createEvent: MaybeContext<
  BuildRESTFunction<typeof publicCreateEvent> & typeof publicCreateEvent
> = /*#__PURE__*/ createRESTModule(publicCreateEvent);
export const bulkCreateEvent: MaybeContext<
  BuildRESTFunction<typeof publicBulkCreateEvent> & typeof publicBulkCreateEvent
> = /*#__PURE__*/ createRESTModule(publicBulkCreateEvent);
export const updateEvent: MaybeContext<
  BuildRESTFunction<typeof publicUpdateEvent> & typeof publicUpdateEvent
> = /*#__PURE__*/ createRESTModule(publicUpdateEvent);
export const bulkUpdateEvent: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateEvent> & typeof publicBulkUpdateEvent
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateEvent);
export const restoreEventDefaults: MaybeContext<
  BuildRESTFunction<typeof publicRestoreEventDefaults> &
    typeof publicRestoreEventDefaults
> = /*#__PURE__*/ createRESTModule(publicRestoreEventDefaults);
export const splitRecurringEvent: MaybeContext<
  BuildRESTFunction<typeof publicSplitRecurringEvent> &
    typeof publicSplitRecurringEvent
> = /*#__PURE__*/ createRESTModule(publicSplitRecurringEvent);
export const cancelEvent: MaybeContext<
  BuildRESTFunction<typeof publicCancelEvent> & typeof publicCancelEvent
> = /*#__PURE__*/ createRESTModule(publicCancelEvent);
export const bulkCancelEvent: MaybeContext<
  BuildRESTFunction<typeof publicBulkCancelEvent> & typeof publicBulkCancelEvent
> = /*#__PURE__*/ createRESTModule(publicBulkCancelEvent);
export const queryEvents: MaybeContext<
  BuildRESTFunction<typeof customQueryEvents> & typeof customQueryEvents
> = /*#__PURE__*/ createRESTModule(customQueryEvents);
/**
 * Triggered when an event is canceled.
 *
 * Not triggered when `INSTANCE` events are canceled. You can use the
 * [Events View API](https://dev.wix.com/docs/rest/business-management/calendar/event-views-v3/introduction)
 * to get notified about `INSTANCE` events.
 */
export const onEventCancelled: BuildEventDefinition<
  typeof publicOnEventCancelled
> &
  typeof publicOnEventCancelled = createEventModule(publicOnEventCancelled);
/**
 * Triggered when an event is created, including when an existing `MASTER`
 * event is split.
 *
 * Not triggered when `INSTANCE` events are created. You can use the
 * [Events View API](https://dev.wix.com/docs/rest/business-management/calendar/event-views-v3/introduction)
 * to get notified about `INSTANCE` events.
 */
export const onEventCreated: BuildEventDefinition<typeof publicOnEventCreated> &
  typeof publicOnEventCreated = createEventModule(publicOnEventCreated);
/**
 * Triggered when a `MASTER` event is split.
 */
export const onEventRecurringSplit: BuildEventDefinition<
  typeof publicOnEventRecurringSplit
> &
  typeof publicOnEventRecurringSplit = createEventModule(
  publicOnEventRecurringSplit
);
/**
 * Triggered when an event is updated, including when it's canceled.
 *
 * Not triggered when `INSTANCE` events are updated. You can use the
 * [Events View API](https://dev.wix.com/docs/rest/business-management/calendar/event-views-v3/introduction)
 * to get notified about `INSTANCE` events.
 */
export const onEventUpdated: BuildEventDefinition<typeof publicOnEventUpdated> &
  typeof publicOnEventUpdated = createEventModule(publicOnEventUpdated);

export {
  Status,
  RecurrenceType,
  Frequency,
  Day,
  Transparency,
  LocationType,
  Role,
  ParticipantsStatus,
  ProviderType,
  Type,
  Field,
  IdentityType,
  WebhookIdentityType,
  SortOrder,
  PlacementType,
  DayOfWeek,
  ResolutionMethod,
  RequestedFields,
} from './calendar-v3-event-events.universal.js';
export {
  Event,
  ZonedDate,
  RecurrenceRule,
  Location,
  Resource,
  Participants,
  Participant,
  ConferencingDetails,
  OnlineMeetingInfo,
  Provider,
  ProviderProviderInfoOneOf,
  Permission,
  CommonIdentificationData,
  CommonIdentificationDataIdOneOf,
  ExtendedFields,
  RecurringEventSplit,
  EventCancelled,
  ParticipantNotification,
  EventUpdatedWithMetadata,
  UpdateScheduleWithFixedBusinessResourceIdRequest,
  UpdateScheduleWithFixedBusinessResourceIdResponse,
  UpdateEventsWithFixedBusinessResourceIdRequest,
  UpdateEventsWithFixedBusinessResourceIdResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  ListEventsByContactIdRequest,
  CommonCursorPaging,
  Sorting,
  ListEventsByContactIdResponse,
  CommonCursorPagingMetadata,
  CommonCursors,
  ListEventsByMemberIdRequest,
  ListEventsByMemberIdResponse,
  SitePropertiesNotification,
  SitePropertiesEvent,
  Properties,
  Categories,
  Locale,
  Address,
  AddressHint,
  GeoCoordinates,
  BusinessSchedule,
  TimePeriod,
  SpecialHourPeriod,
  Multilingual,
  SupportedLanguage,
  ConsentPolicy,
  Translation,
  ChangeContext,
  ChangeContextPayloadOneOf,
  PropertiesChange,
  SiteCreated,
  SiteCloned,
  Empty,
  GetEventRequest,
  GetEventResponse,
  ListEventsRequest,
  ListEventsResponse,
  QueryEventsRequest,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  CursorPaging,
  QueryEventsResponse,
  CursorPagingMetadata,
  Cursors,
  ListRecurringEventInstancesHistoryRequest,
  ListRecurringEventInstancesHistoryResponse,
  CreateEventRequest,
  CreateEventResponse,
  BulkCreateEventRequest,
  MaskedEvent,
  BulkCreateEventResponse,
  BulkEventResult,
  ItemMetadata,
  ApplicationError,
  BulkActionMetadata,
  UpdateEventRequest,
  UpdateEventResponse,
  BulkUpdateEventRequest,
  BulkUpdateEventRequestMaskedEvent,
  BulkUpdateEventResponse,
  UpdateEventParticipantsRequest,
  UpdateEventParticipantsResponse,
  RestoreEventDefaultsRequest,
  RestoreEventDefaultsResponse,
  SplitRecurringEventRequest,
  SplitRecurringEventResponse,
  CancelEventRequest,
  CancelEventResponse,
  BulkCancelEventRequest,
  BulkCancelEventResponse,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  EventCancelledEnvelope,
  EventCreatedEnvelope,
  EventRecurringSplitEnvelope,
  EventUpdatedEnvelope,
  ListEventsByContactIdOptions,
  ListEventsByMemberIdOptions,
  GetEventOptions,
  ListEventsOptions,
  QueryEventsOptions,
  EventsQueryResult,
  EventsQueryBuilder,
  EventQuerySpec,
  CreateEventOptions,
  BulkCreateEventOptions,
  UpdateEvent,
  UpdateEventOptions,
  BulkUpdateEventOptions,
  RestoreEventDefaultsOptions,
  SplitRecurringEventOptions,
  CancelEventOptions,
  BulkCancelEventOptions,
} from './calendar-v3-event-events.universal.js';
export { utils } from './calendar-v3-event-events.universal.js';
export {
  StatusWithLiterals,
  RecurrenceTypeWithLiterals,
  FrequencyWithLiterals,
  DayWithLiterals,
  TransparencyWithLiterals,
  LocationTypeWithLiterals,
  RoleWithLiterals,
  ParticipantsStatusWithLiterals,
  ProviderTypeWithLiterals,
  TypeWithLiterals,
  FieldWithLiterals,
  IdentityTypeWithLiterals,
  WebhookIdentityTypeWithLiterals,
  SortOrderWithLiterals,
  PlacementTypeWithLiterals,
  DayOfWeekWithLiterals,
  ResolutionMethodWithLiterals,
  RequestedFieldsWithLiterals,
  CancelEventApplicationErrors,
  BulkCancelEventApplicationErrors,
  CommonQueryWithEntityContext,
  EventQuery,
} from './calendar-v3-event-events.universal.js';
